// "Add to this listing" — the single quiet upsell surface of the product
// (CREDITS_FLOW_DESIGN v3 §4 "Working", D20). One section, shared by the
// editor and the share page, listing what the owner has not bought as plain
// text lines with a price and an "Add" button each:
//
//   Add to this listing
//   Branded PDF · 100 credits                                   [Add]
//   Seller Presentation · 100 credits · with the editable PowerPoint   [Add]
//   One-pager · 50 credits                                      [Add]
//   Social set · 100 credits · captions for the listing's social posts [Add]
//   Photo touch-ups · 15 credits a photo                        [Choose photos]
//
// Nothing is rendered speculatively: a file is rendered when it is bought
// ("Preparing your Branded PDF — about a minute"), the social set is content
// served on the next read. No lock icons, no previews of unbought files, no
// cost badges anywhere else. Bought items simply stop being listed here and
// show up where files live; the word "included" appears nowhere.
//
// The touch-up picker lives here too: the candidates and the touch-up POST
// are keyed by the computation, and the before/after gallery only exists once
// a photo succeeded, so this section — not the gallery — is where a set is
// ordered.

import axios from "axios";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { API_URL } from "../../constants";
import {
  ACTION_COSTS,
  ARTIFACT_LABELS,
  ARTIFACT_NOTES,
  formatCredits,
  formatCreditsNumber,
  insufficientCreditsFrom,
  takeCreditsResume,
} from "../../constants/credits";
import TouchupPicker from "../../containers/TouchupsPage/TouchupPicker";
import AccountContext, { useAccountState } from "../../contexts/AccountContext";
import usePaidAttempt from "../../hooks/usePaidAttempt";
import usePollReportDocument from "../../hooks/usePollReportDocument";
import { track } from "../../utils/analytics";
import { artifactUsable, exportJobUnit, exportUnitMessage } from "../../utils/artifactQuality";
import { authHeader, getToken } from "../../utils/auth";
import { paidAttemptCurrentJob } from "../../utils/paidAttempts";

import ConfirmSpendSheet from "./ConfirmSpendSheet";
import CreditsPaywallSheet from "./CreditsPaywallSheet";

/**
 * `useAccount()` that never fetches while the flag is off. The shared hook
 * fetches /auth/me/ for itself when no provider is mounted (every page test
 * renders without one); the working surfaces must keep their flag-off network
 * behaviour byte-identical, so they read the account through this instead.
 */
export const useCreditsAccount = () => {
  const ctx = useContext(AccountContext);
  const local = useAccountState({ enabled: !ctx });
  return ctx || local;
};

/** Manifest key a bought artifact lands under (mirror of entitlements.RENDER_NAME). */
/** @type {Record<string, string>} */
const MANIFEST_KEY = { branded_pdf: "report_pdf", deck: "deck", one_pager: "one_pager" };

/** @type {Record<string, string>} */
const ADD_TITLES = {
  branded_pdf: "Add a Branded PDF",
  deck: "Add a Seller Presentation",
  one_pager: "Add a one-pager",
  social_set: "Add the social set",
};

/** @type {Record<string, string>} */
const ADD_NOTES = {
  branded_pdf: "Takes about a minute.",
  deck: "Includes the editable PowerPoint. Takes about a minute.",
  one_pager: "Takes about a minute.",
  social_set: "The captions appear right away.",
};

/** How long to wait for a bought file before saying so (renders take ~a minute). */
const ADD_POLL_MAX_MS = 4 * 60 * 1000;

/**
 * Is this a credits-funded listing? The owner payload carries `reprepare`
 * (the re-prepare quote) only for metered listings, and `addable` is empty for
 * legacy ones — either signal means the section applies.
 * @param {Record<string, any> | null | undefined} doc
 */
export const isCreditsListing = (doc) =>
  !!doc && (!!doc.reprepare || (Array.isArray(doc.addable) && doc.addable.length > 0));

/**
 * Has this listing bought the named add-on?
 *
 * `addable` lists what is still for sale, so an artifact appearing there has
 * not been bought. A legacy listing has nothing for sale and keeps everything.
 * @param {Record<string, any> | null | undefined} doc
 * @param {string} artifact
 */
export const artifactBought = (doc, artifact) =>
  !isCreditsListing(doc) ||
  !(Array.isArray(doc?.addable) ? doc.addable : []).some((item) => item?.artifact === artifact);

/**
 * The bought file has landed when its manifest entry reads available.
 * @param {Record<string, any> | null | undefined} doc
 * @param {string} artifact
 */
export const artifactRendered = (doc, artifact) => {
  const key = MANIFEST_KEY[artifact];
  return (
    !!key &&
    artifactUsable(doc?.artifacts?.[key], key) &&
    (artifact !== "deck" || artifactUsable(doc?.artifacts?.pptx, "pptx"))
  );
};

/** @param {string} artifact */
const artifactLabel = (artifact) => ARTIFACT_LABELS[artifact] || artifact;

/** Content-on-purchase (the social set): nothing renders, it is served on the next read. */
const isContentAddon = (/** @type {string} */ artifact) => !MANIFEST_KEY[artifact];

const Section = styled.section`
  margin: 8px 0 18px;
  padding: 14px 16px;
  border: 1px solid var(--brand-border, #e3ebec);
  border-radius: 12px;
  background: #ffffff;
`;

const Title = styled.h3`
  margin: 0 0 6px;
  font-size: 0.85rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
`;

const Line = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 0;
  border-top: 1px solid var(--brand-border, #e3ebec);

  &:first-of-type {
    border-top: 0;
  }
`;

const LineText = styled.div`
  font-size: 0.92rem;
  color: var(--brand-ink, #0b1c1e);
  line-height: 1.45;

  span {
    color: var(--brand-ink-3, #5c7074);
  }
`;

const QuietText = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: var(--brand-ink-3, #5c7074);
  line-height: 1.45;
`;

const LineError = styled.p`
  margin: 4px 0 0;
  font-size: 0.86rem;
  color: #b91c1c;
`;

const AddButton = styled.button`
  background: none;
  border: 1px solid var(--brand-primary, #0e7e7a);
  color: var(--brand-primary-700, #085552);
  border-radius: 999px;
  padding: 7px 16px;
  font-size: 0.86rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  min-height: 36px;

  &:hover:not(:disabled) {
    background: var(--brand-primary-50, #e6f3f2);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

/**
 * @typedef {{ kind: "rendering" | "queued" | "ready" | "error", text: string }} LineState
 */

/**
 * @param {{
 *   uuid: string,
 *   doc: Record<string, any> | null | undefined,
 *   onDocument: (fresh: Record<string, any>) => void,
 *   variant?: "editor" | "share",
 * }} props
 *   uuid: the computation uuid (the report-document and touch-up endpoints).
 *   doc: the owner report-document payload (`addable`, `listing_uuid`,
 *     `photo_manifest`, `reprepare`).
 *   onDocument: hands the parent a fresh payload once a bought file or the
 *     social set has landed — the parent decides what to merge.
 *   variant: where the bought file can be found afterwards.
 */
const AddToListing = ({ uuid, doc, onDocument, variant = "editor" }) => {
  if (!doc?.editable || !isCreditsListing(doc)) return null;
  return (
    <LiveAddToListing key={uuid} uuid={uuid} doc={doc} onDocument={onDocument} variant={variant} />
  );
};

/**
 * @param {{
 *   uuid: string,
 *   doc: Record<string, any>,
 *   onDocument: (fresh: Record<string, any>) => void,
 *   variant: "editor" | "share",
 * }} props
 */
const LiveAddToListing = ({ uuid, doc, onDocument, variant }) => {
  const account = useCreditsAccount();
  const listingUuid = typeof doc.listing_uuid === "string" ? doc.listing_uuid : "";
  const addable = useMemo(
    () =>
      /** @type {{artifact: string, cost: number}[]} */ (
        Array.isArray(doc.addable)
          ? doc.addable.filter((item) => item && typeof item.artifact === "string")
          : []
      ),
    [doc.addable]
  );
  const photoCount = Object.keys(doc.photo_manifest || {}).length;
  const showTouchups = photoCount > 0;
  const [confirm, setConfirm] = useState(
    /** @type {{artifact: string, cost: number} | null} */ (null)
  );
  const [busy, setBusy] = useState(false);
  const [confirmError, setConfirmError] = useState(/** @type {string | null} */ (null));
  const [lines, setLines] = useState(/** @type {Record<string, LineState>} */ ({}));
  const [rendering, setRendering] = useState(
    /** @type {{artifact: string, jobId: string, computationUuid:string} | null} */ (null)
  );
  // The paywall, with the blocked Add riding its return stash so a top-up
  // comes back to the same confirm.
  const [block, setBlock] = useState(
    /** @type {{ block: {balance: number, cost: number, action: string}, resume: { kind: "add", artifact: string } } | null} */ (
      null
    )
  );
  const [pickerOpen, setPickerOpen] = useState(false);
  // Back from a pack bought at a blocked tap: the confirm (or the picker)
  // reopens with the same choice and one fact line, "Credits added — you
  // have N", shown once.
  const [creditsAdded, setCreditsAdded] = useState(
    /** @type {{ balance: number | null } | null} */ (null)
  );
  const [pickerResume, setPickerResume] = useState(
    /** @type {{ photos: string[], balance: number | null } | null} */ (null)
  );
  const mountedRef = useRef(true);
  const activeUuidRef = useRef(uuid);
  activeUuidRef.current = uuid;
  const resumedRef = useRef(false);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  const refreshAccount = account.refresh;
  const balance = account.credits ? account.credits.balance : null;

  // The mount guard keeps return confirmation one-shot even when props refresh.
  useEffect(() => {
    if (resumedRef.current) return;
    resumedRef.current = true;
    const stash = takeCreditsResume(listingUuid || undefined);
    if (!stash) return;
    if (stash.resume.kind === "add") {
      const item = addable.find((entry) => entry.artifact === stash.resume.artifact);
      if (!item) return;
      setCreditsAdded({ balance: stash.balance ?? null });
      setConfirmError(null);
      setConfirm(item);
      void refreshAccount();
    } else if (stash.resume.kind === "touchups") {
      const photos = Array.isArray(stash.resume.photos)
        ? stash.resume.photos.filter((photo) => typeof photo === "string")
        : [];
      if (photos.length === 0) return;
      setPickerResume({ photos, balance: stash.balance ?? null });
      setPickerOpen(true);
    }
  }, [addable, listingUuid, refreshAccount]);

  const creditsAddedLine = creditsAdded
    ? (() => {
        const known = balance ?? creditsAdded.balance;
        return known === null || known === undefined
          ? "Credits added."
          : `Credits added — you have ${formatCreditsNumber(known)}.`;
      })()
    : null;

  /** @param {string} artifact @param {LineState | null} state */
  const setLine = useCallback(
    (/** @type {string} */ artifact, /** @type {LineState | null} */ state) => {
      setLines((prev) => {
        const next = { ...prev };
        if (state) next[artifact] = state;
        else delete next[artifact];
        return next;
      });
    },
    []
  );

  const openConfirm = useCallback(
    (/** @type {{artifact: string, cost: number}} */ item) => {
      setConfirmError(null);
      setConfirm(item);
      void refreshAccount();
    },
    [refreshAccount]
  );

  /** @param {string} artifact */
  const readyText = useCallback(
    (/** @type {string} */ artifact) => {
      const label = artifactLabel(artifact);
      if (isContentAddon(artifact)) {
        return variant === "editor"
          ? "Social set added — the captions are on your Share page."
          : "Social set added — the captions are below.";
      }
      return variant === "editor"
        ? `${label} is ready — download it from Share.`
        : `${label} is ready.`;
    },
    [variant]
  );

  const paidRef = useRef(/** @type {ReturnType<typeof usePaidAttempt>|null} */ (null));
  const [needsAttemptCheck, setNeedsAttemptCheck] = useState(false);
  const [attemptReportUuid, setAttemptReportUuid] = useState("");
  const trackedAttemptRef = useRef("");
  const paid = usePaidAttempt({
    // One unresolved addition per listing. Its frozen body identifies the
    // artifact even after it disappears from the current addable menu.
    scope: { action: "add", targetUuid: listingUuid },
    enabled: !!listingUuid && !!getToken() && doc.editable === true,
    onAccepted: async (receipt, record) => {
      const artifact = record.request.artifact;
      const job = paidAttemptCurrentJob(receipt);
      const computationUuid = job?.computation_uuid || receipt.attempt.computation_uuid;
      setConfirm(null);
      setCreditsAdded(null);
      if (trackedAttemptRef.current !== record.attempt_id) {
        trackedAttemptRef.current = record.attempt_id;
        track("credits_spent", {
          action: artifact,
          cost: receipt.settlement.charged_credits,
          attempt_id: record.attempt_id,
        });
      }
      void refreshAccount();
      if (
        !receipt.replacement_chain_complete ||
        !computationUuid ||
        (receipt.attempt.job_id && !job)
      ) {
        setLine(artifact, {
          kind: "error",
          text: "This addition was accepted, but its current result is not available yet. Check status again.",
        });
        return;
      }
      setAttemptReportUuid(computationUuid !== uuid ? computationUuid : "");
      if (job && ["queued", "processing"].includes(job.status)) {
        setLine(artifact, {
          kind: "rendering",
          text: `Preparing your ${artifactLabel(artifact)} — about a minute`,
        });
        setRendering({ artifact, jobId: job.job_id, computationUuid });
        return;
      }
      const requestUuid = uuid;
      const token = getToken();
      let fresh = null;
      try {
        const response = await axios.get(
          `${API_URL}/computations/${computationUuid}/report-document/`,
          {
            headers: authHeader(),
            timeout: 10000,
          }
        );
        if (!mountedRef.current || activeUuidRef.current !== requestUuid || getToken() !== token)
          return;
        const candidate = response.data;
        if (
          !candidate ||
          candidate.editable !== true ||
          candidate.listing_uuid !== record.target_uuid ||
          !Number.isSafeInteger(candidate.version) ||
          candidate.version < 1
        )
          throw new Error("The current owner report could not be verified.");
        fresh = candidate;
        if (computationUuid === uuid) onDocument(fresh);
      } catch {
        /* Exact job settlement remains useful without current downloads. */
      }
      if (!mountedRef.current || activeUuidRef.current !== requestUuid || getToken() !== token)
        return;
      const unit = job ? exportJobUnit(job, artifact, job.job_id) : null;
      const refund = receipt.settlement.refunded_credits;
      const refundText = refund > 0 ? ` ${formatCredits(refund)} returned for this request.` : "";
      if (unit?.status === "held") {
        // This request can reuse an older job at zero charge. Never borrow
        // that older job's refund counters for this request's financial copy.
        const outcomeOnly = { ...unit, settlement: { charged_credits: 0, refunded_credits: 0 } };
        setLine(artifact, {
          kind: "error",
          text: exportUnitMessage(outcomeOnly, { requested: true }) + refundText,
        });
      } else if (job?.status === "failed") {
        setLine(artifact, {
          kind: "error",
          text: `This ${artifactLabel(artifact)} attempt did not finish.${refundText}`,
        });
      } else if (
        (job?.status === "completed" && artifactRendered(fresh, artifact)) ||
        (!job &&
          fresh &&
          (isContentAddon(artifact)
            ? Array.isArray(fresh.social_posts) &&
              fresh.social_posts.some(
                (/** @type {any} */ post) =>
                  post?.locked !== true && typeof post?.text === "string" && post.text.trim()
              )
            : artifactRendered(fresh, artifact)))
      ) {
        setLine(artifact, { kind: "ready", text: readyText(artifact) });
      } else if (job?.status === "completed" && unit?.status === "delivered") {
        setLine(artifact, {
          kind: "error",
          text: `Your requested ${artifactLabel(artifact)} export completed. Its current download could not be verified; refresh the report to check it.`,
        });
      } else {
        setLine(artifact, {
          kind: "error",
          text: "This addition was accepted, but we could not verify its output. Check status again.",
        });
        return;
      }
      paidRef.current?.acknowledge(record.attempt_id);
    },
    onRejected: (receipt, record) => {
      const artifact = record.request.artifact;
      const response = receipt.attempt.response;
      if (!paidRef.current?.acknowledge(record.attempt_id)) return;
      setConfirm(null);
      setCreditsAdded(null);
      const insufficient = insufficientCreditsFrom({
        response: { status: response.status_code, data: response.data },
      });
      if (
        insufficient &&
        [insufficient.balance, insufficient.cost].every(
          (value) => Number.isSafeInteger(value) && value >= 0
        )
      ) {
        setBlock({ block: insufficient, resume: { kind: "add", artifact } });
        return;
      }
      setLine(artifact, {
        kind: "error",
        text:
          typeof response.data?.error === "string"
            ? response.data.error
            : "This addition was not accepted. Review it before trying again.",
      });
    },
  });
  paidRef.current = paid;
  const { submit: submitAddition, check: checkAddition, busy: checkingAddition } = paid;

  const confirmAdd = useCallback(async () => {
    if (!confirm || busy) return;
    setBusy(true);
    setConfirmError(null);
    try {
      await submitAddition({ artifact: confirm.artifact });
    } finally {
      if (mountedRef.current) {
        setBusy(false);
        setConfirm(null);
      }
    }
  }, [confirm, busy, submitAddition]);

  // Job polling observes work, but only the exact purchase receipt can settle
  // THIS request. Keep its browser record across timeouts and permission loss.
  usePollReportDocument({
    active: rendering != null,
    uuid: rendering?.computationUuid || uuid,
    jobId: rendering?.jobId || null,
    isReady: () => true,
    onReady: () => {
      setRendering(null);
      setNeedsAttemptCheck(true);
    },
    onFailed: () => {
      setRendering(null);
      setNeedsAttemptCheck(true);
    },
    onTimeout: () => {
      const artifact = rendering?.artifact || "";
      setRendering(null);
      setLine(artifact, {
        kind: "error",
        text: `Your ${artifactLabel(artifact)} is taking longer than expected. Check the saved request's status.`,
      });
    },
    maxMs: ADD_POLL_MAX_MS,
  });
  useEffect(() => {
    if (!needsAttemptCheck || checkingAddition) return;
    setNeedsAttemptCheck(false);
    void checkAddition();
  }, [needsAttemptCheck, checkingAddition, checkAddition]);

  const addableKeys = new Set(addable.map((item) => item.artifact));
  const finishedLines = Object.entries(lines).filter(
    ([artifact, state]) => !addableKeys.has(artifact) && ["ready", "error"].includes(state.kind)
  );

  if (
    addable.length === 0 &&
    !showTouchups &&
    finishedLines.length === 0 &&
    !paid.pending &&
    !paid.error
  )
    return null;

  return (
    <Section
      id="add-to-listing"
      aria-labelledby="add-to-listing-title"
      data-testid="add-to-listing"
    >
      <Title id="add-to-listing-title">Add to this listing</Title>
      {addable.map((item) => {
        const label = artifactLabel(item.artifact);
        const note = ARTIFACT_NOTES[item.artifact];
        const state = lines[item.artifact];
        if (state && state.kind !== "error") {
          return (
            <Line key={item.artifact}>
              <QuietText as="output" aria-live="polite">
                {state.text}
              </QuietText>
            </Line>
          );
        }
        return (
          <Line key={item.artifact}>
            <div>
              <LineText>
                {label} <span>· {formatCredits(item.cost)}</span>
                {note && <span> · {note}</span>}
              </LineText>
              {state?.kind === "error" && <LineError role="alert">{state.text}</LineError>}
            </div>
            <AddButton
              type="button"
              onClick={() => openConfirm(item)}
              disabled={busy || paid.busy || !!paid.pending || rendering != null}
              aria-label={`Add ${label}`}
            >
              Add
            </AddButton>
          </Line>
        );
      })}
      {finishedLines.map(([artifact, state]) => (
        <Line key={artifact}>
          <QuietText as="output" aria-live="polite">
            {state.text}
          </QuietText>
        </Line>
      ))}
      {paid.pending && (
        <Line>
          <div aria-live="polite">
            <QuietText>
              This addition request is saved. Check its exact status before adding another item.
            </QuietText>
            <AddButton type="button" disabled={paid.busy} onClick={() => void paid.check()}>
              {paid.busy ? "Checking status…" : "Check addition status"}
            </AddButton>
          </div>
        </Line>
      )}
      {paid.error && <LineError role="alert">{paid.error}</LineError>}
      {attemptReportUuid && (
        <Link to={`/reports/${attemptReportUuid}/share`}>Open the report for this addition</Link>
      )}
      {showTouchups && !pickerOpen && (
        <Line>
          <LineText>
            Photo touch-ups <span>· {formatCredits(ACTION_COSTS.photo_touchup)} a photo</span>
          </LineText>
          <AddButton type="button" onClick={() => setPickerOpen(true)}>
            Choose photos
          </AddButton>
        </Line>
      )}
      {pickerOpen && (
        <TouchupPicker
          uuid={uuid}
          doc={doc}
          balance={balance}
          resume={pickerResume || undefined}
          onSpendOpen={refreshAccount}
          onSpent={refreshAccount}
          onDocument={onDocument}
          onClose={() => {
            setPickerOpen(false);
            setPickerResume(null);
          }}
          galleryLink={(path) => <Link to={path}>See the before-and-after gallery</Link>}
        />
      )}
      {confirm && (
        <ConfirmSpendSheet
          title={ADD_TITLES[confirm.artifact] || `Add ${artifactLabel(confirm.artifact)}`}
          cost={confirm.cost}
          balance={balance}
          note={creditsAddedLine || ADD_NOTES[confirm.artifact]}
          busy={busy}
          error={confirmError}
          onConfirm={confirmAdd}
          onClose={() => {
            if (busy) return;
            setConfirm(null);
            setCreditsAdded(null);
          }}
        />
      )}
      {block && (
        <CreditsPaywallSheet
          block={block.block}
          resume={block.resume}
          onClose={() => setBlock(null)}
          listingUuid={listingUuid || undefined}
        />
      )}
    </Section>
  );
};

export default AddToListing;
